import type React from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import type { IconProps } from '../icons/types'
import { cn, cva, type VariantProps } from '../lib/cva'
import { Spinner } from './Spinner'

const buttonVariants = cva(
	'relative flex flex-row items-center justify-center gap-x-1 py-3 text-base rounded-lg font-medium transition-colors focus:outline-none disabled:cursor-not-allowed cursor-pointer whitespace-nowrap',
	{
		variants: {
			variant: {
				primary: 'bg-primary-600 text-neutral-50 hover:bg-primary-800 disabled:bg-primary-300',
				destructive: 'bg-error-600 text-neutral-50 hover:bg-error-800 disabled:bg-error-300',
				secondary:
					'bg-neutral-0 border border-neutral-300 text-neutral-800 hover:bg-neutral-50 hover:border-neutral-500 disabled:text-neutral-300 disabled:bg-neutral-200 disabled:border-neutral-100',
				'outline-destructive':
					'bg-neutral-0 border border-error-600 text-error-600 hover:bg-error-600 hover:text-neutral-50 disabled:border-error-300 disabled:bg-neutral-200 ',
				tertiary:
					'bg-neutral-0 text-neutral-800 hover:bg-neutral-200 disabled:text-neutral-300 disabled:bg-neutral-200',
			},
			size: {
				full: 'w-full',
				medium: 'px-5 h-13',
				small: 'px-3 py-2 text-sm',
			},
		},
		defaultVariants: {
			variant: 'primary',
			size: 'medium',
		},
	},
)

const iconVariants = cva('fill-neutral-0', {
	variants: {
		variant: {
			primary: 'fill-neutral-0',
			destructive: 'fill-neutral-0',
			'outline-destructive': 'fill-neutral-0',
			secondary: 'fill-neutral-800',
			tertiary: 'fill-neutral-800',
		},
		disabled: {
			true: '',
			false: '',
		},
	},
	compoundVariants: [
		{
			variant: ['secondary', 'tertiary'],
			disabled: true,
			className: 'fill-neutral-300',
		},
	],
	defaultVariants: {
		variant: 'primary',
	},
})

const spinnerVariants = cva('text-neutral-0 size-5 absolute', {
	variants: {
		variant: {
			primary: 'text-neutral-0',
			destructive: 'text-neutral-0',
			'outline-destructive': 'text-neutral-0',
			secondary: 'text-neutral-800',
			tertiary: 'text-neutral-800',
		},
	},
})

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	icon?: React.FC<IconProps>
	iconPosition?: 'left' | 'right'
	children: ReactNode
	isLoading?: boolean
	'data-testid'?: string
}

export const Button: React.FC<ButtonProps> = ({
	variant,
	size,
	icon: Icon,
	iconPosition = 'left',
	children,
	className,
	type = 'button',
	isLoading = false,
	...props
}) => {
	return (
		<button
			className={cn(buttonVariants({ variant, size }), className)}
			{...props}
			disabled={isLoading || props.disabled}
			type={type}>
			{Icon && iconPosition === 'left' && <Icon className={iconVariants({ variant, disabled: props.disabled })} />}
			<span className={cn('inline-flex items-center', isLoading && 'opacity-0')}>{children}</span>
			{Icon && iconPosition === 'right' && <Icon className={iconVariants({ variant, disabled: props.disabled })} />}
			{isLoading && <Spinner className={spinnerVariants({ variant })} />}
		</button>
	)
}
